// © 2026 AttainIP Engineering

/**
 * Auth State Store
 * Manages authentication state with in-memory token storage per RFC-0202
 */

import { create } from 'zustand';

import { appConfig } from '../config/appConfig';
import { orgIdentity, UNRESOLVED_ORG_LABEL } from '../lib/orgChoiceLabel';

export interface User {
  id: string;
  email: string;
  display_name: string;
  organization_id: string;
  roles: string[];
  /** RFC-0500/RFC-0121 A4: the account's real audience(s) — `enterprise` or
   *  `selfservice`. Optional because some legacy/partial user shapes in
   *  tests omit it; a missing value is treated as not-enterprise
   *  (`isEnterpriseAccount` fails closed). */
  audiences?: string[];
  /** RFC-0510 W06 — the acting organisation's human-readable identity, carried
   *  on the session the SAME way `features` is (RFC-0500 A5) so the active-
   *  organisation indicator renders SYNCHRONOUSLY on first paint and never
   *  flickers in after a fetch. `org_name` is preferred; `org_ext_id` (e.g.
   *  "enterprise-domain:hpdlaw.com") is a readable fallback. Both optional and
   *  nullable — the indicator fails closed to showing nothing, never a UUID.
   *  See `activeOrgLabel`. */
  org_name?: string | null;
  org_ext_id?: string | null;
  /** The acting org's resolved product configuration (RFC-0500 A5), carried on
   *  the session so route guards stay SYNCHRONOUS — see `isWorkspaceEnabled`.
   *
   *  ⚠️ Tri-state and UNRESOLVED. `undefined`/`null` means the firm has no
   *  override, so the ENVIRONMENT default decides. The server cannot resolve
   *  this for us: it does not know which environment this client is. */
  features?: {
    /** null/undefined = unset → fall back to the runtime env flag. */
    workspace?: boolean | null;
    /** RFC-0511 B — the firm's per-org SET of enabled workflow keys.
     *  null/undefined = unset → the ENVIRONMENT's catalogue decides and the
     *  client must not filter. `[]` is a different answer: an explicit "none
     *  of them". See `isWorkflowEnabledForOrg`. */
    workflows?: string[] | null;
  };
}

// Namespaced per RFC-0503 so an enterprise and a self-service SPA on the same
// origin don't share a session via a collided localStorage key. For the default
// selfserve flavour `storageNamespace` is `attain_`, so this stays byte-identical
// to the historical `attain_refresh_token` key.
const REFRESH_TOKEN_KEY = `${appConfig.storageNamespace}refresh_token`;

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  expiresAt: number | null;

  // Actions
  setAuth: (data: {
    user: User;
    accessToken: string;
    expiresIn: number;
    refreshToken?: string;
  }) => void;
  setUser: (user: User) => void;
  setAccessToken: (token: string) => void;
  logout: () => void;
  isTokenExpired: () => boolean;
  setLoading: (loading: boolean) => void;
}

const initialState = {
  user: null as User | null,
  accessToken: null as string | null,
  isAuthenticated: false,
  isLoading: false,
  expiresAt: null as number | null,
};

export const useAuthStore = create<AuthState>((set, get) => ({
  ...initialState,

  setAuth: ({ user, accessToken, expiresIn, refreshToken }) => {
    const expiresAt = Date.now() + expiresIn * 1000;
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    set({
      isAuthenticated: true,
      user,
      accessToken,
      expiresAt,
    });
  },

  setUser: (user) => {
    set({ user });
  },

  setAccessToken: (token: string) => {
    set({ accessToken: token });
  },

  logout: () => {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    set(initialState);
  },

  isTokenExpired: () => {
    const { expiresAt } = get();
    if (!expiresAt) return true;
    // Consider expired 30 seconds before actual expiry
    return Date.now() > expiresAt - 30000;
  },

  setLoading: (loading) => {
    set({ isLoading: loading });
  },
}));

/** Read persisted refresh token (survives full page reloads). */
export function getStoredRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/** True when the user holds the `org_admin` role (RFC-0121) — an enterprise
 *  firm admin managing their own firm's team/pool/login-policy (RFC-0211 A2).
 *  Distinct from `platform_admin`, which is Attain-staff-only and lives in
 *  the ops console, never this product. */
export function isOrgAdmin(user: User | null): boolean {
  return !!user?.roles?.includes('org_admin');
}

/** True when the user's actual account is enterprise (RFC-0500/RFC-0121 A4)
 *  — the JWT/backend `audiences` claim, NOT `appConfig.accountType` (which
 *  is the build's flavour, not the logged-in user's real account). A self-
 *  serve user must never be treated as enterprise just because they're
 *  sitting on the enterprise build (the bug this closes). */
export function isEnterpriseAccount(user: User | null): boolean {
  return !!user?.audiences?.includes('enterprise');
}

/** What the active-organisation indicator states about this session. */
export interface ActiveOrgIndicator {
  /** Always non-empty, and safe to render — never a UUID, never a placeholder. */
  label: string;
  /** False when nothing could be resolved and `label` says so out loud. */
  resolved: boolean;
}

/** RFC-0510 W06 + W39 — what the active-organisation indicator shows, or `null`
 *  when this session has no indicator at all.
 *
 *  W06 wrote this as a SAFETY control that FAILS CLOSED — "a wrong or absent
 *  label is worse than none" — and returned `null` in two cases. That was
 *  correct while tenancy could not change mid-session. Under W39's acting-in
 *  control it is not: "no badge" becomes indistinguishable from "in my own
 *  firm", and an attorney working inside a client's tenancy without knowing it
 *  is a confidentiality and conflicts problem, not a cosmetic one.
 *
 *  So W39 NARROWS when nothing is shown; it does not remove the caution:
 *
 *   - NON-ENTERPRISE (including missing audiences) → still `null`, and for
 *     W06's original reason, which still holds: a self-serve session sits in one
 *     personal organisation, a persistent badge there is noise that trains
 *     people to ignore the indicator — and, decisively, THE SELF-SERVE SURFACE
 *     HAS NO SWITCHER (W39 is enterprise-only, precisely because it has no
 *     indicator). Nothing can change tenancy there, so nothing has to be
 *     watched. Reuses `isEnterpriseAccount`, which fails closed.
 *   - ENTERPRISE → NEVER `null`. Prefer `org_name`; fall back to a readable
 *     `org_ext_id` ("enterprise-domain:hpdlaw.com" → "hpdlaw.com"); and where
 *     neither resolves — a bare UUID, or Identity's `Organization (ce75cb74)`
 *     placeholder — say `Unknown organisation` LOUDLY rather than disappear.
 *     A name is still never invented, and a UUID is still never rendered as one.
 *
 *  The resolution chain is `orgIdentity`, shared with the switcher's rows, so
 *  the badge and the control can never disagree about what an organisation is
 *  called. */
export function activeOrgIndicator(user: User | null): ActiveOrgIndicator | null {
  if (!isEnterpriseAccount(user)) return null;

  const { label, resolved } = orgIdentity({
    // The indicator has no org_type to work from — `/v1/auth/me` carries the
    // acting org's name and ext_id, not its kind — so the chain runs on those
    // two, which is exactly what W06 used.
    organization_id: user?.organization_id ?? '',
    name: user?.org_name,
    org_ext_id: user?.org_ext_id,
  });
  return { label, resolved };
}

/** RFC-0510 W06 — the label the indicator shows, or `null` where there is no
 *  indicator (non-enterprise). Kept as the narrow accessor the header wrappers
 *  already use; `activeOrgIndicator` is the one that also says whether the name
 *  was resolved, which is what decides how loudly it is rendered.
 *
 *  ⚠️ On an enterprise session this is now NEVER `null` — an unresolvable
 *  organisation reads `Unknown organisation`. See `activeOrgIndicator`. */
export function activeOrgLabel(user: User | null): string | null {
  return activeOrgIndicator(user)?.label ?? null;
}

/** Re-exported so a caller rendering the indicator can name the unresolved
 *  state without reaching past the store into the label module. */
export { UNRESOLVED_ORG_LABEL };
